import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { type } from "arktype";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { AuthCard } from "@/components/auth/auth-card";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { Link } from "@/components/ui/link";
import { useAppForm } from "@/hooks/useCreateForm";
import { authClient } from "@/lib/authClient";

const searchParams = type({
  error: "string?",
  token: "string?", // INVALID_TOKEN for both expired and inaccurate
});

const passwordValidator = type({
  confirm: "string >= 8",
  password: "string >= 8",
}).narrow((data, ctx) => {
  if (data.password === data.confirm) {
    return true;
  }
  return ctx.reject({
    actual: "",
    expected: "identical to password",
    path: ["confirm"],
  });
});

export const Route = createFileRoute("/_unauthed/reset-password")({
  component: RouteComponent,
  validateSearch: searchParams,
});

function RouteComponent() {
  const navigate = Route.useNavigate();
  const params = Route.useSearch();

  const { mutate, data, isPending } = useMutation({
    mutationFn: ({ newPassword }: { newPassword: string }) =>
      authClient.resetPassword({
        newPassword,
        token: params.token,
      }),

    onSettled: (result) => {
      if (result?.error) {
        toast.error("Unable to reset password", {
          description: "Try requesting a new reset password link",
        });
      }
    },
  });

  const form = useAppForm({
    defaultValues: {
      confirm: "",
      password: "",
    },
    onSubmit: ({ value }) => {
      mutate({ newPassword: value.password });
    },
    onSubmitInvalid: () => {
      console.log("invalid");
    },
    validators: {
      onSubmit: passwordValidator,
    },
  });

  if (params.token === undefined || params.error) {
    navigate({
      search: {
        error: "Invalid or expired reset password link",
      },
      to: "/forgot-password",
    });
    return;
  }

  if (data?.data?.status) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <AuthCard
          description="You can now log in with your new password"
          title="Password Reset"
        >
          <div className="flex justify-end">
            <Link to="/login" variant="default">
              Back to login
            </Link>
          </div>
        </AuthCard>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <AuthCard
          description="Enter your new password below"
          title="Reset Password"
        >
          <Form>
            <form.AppField
              children={(field) => {
                return (
                  <field.PasswordField label="Password" required={false} />
                );
              }}
              name="password"
            />
            <form.AppField
              children={(field) => {
                return (
                  <field.PasswordField
                    label="Confirm Password"
                    required={false}
                  />
                );
              }}
              name="confirm"
            />
            <Button className="w-full" disabled={isPending} type="submit">
              {isPending && <Loader2 className="mr-2 animate-spin" size={16} />}
              Reset Password
            </Button>
          </Form>
        </AuthCard>
      </form>
    </div>
  );
}
